/**
 * Prevents a tooltip from overflowing the container it is in.
 * Note that for short container sizes, it may still overflow the bottom of the container.
 *
 * mousePosition param represents the mouse coordinates and must be in this format: { x: 20, y: 20 }
 * tooltipId and containerId are the ids on their respective DOM elements
 *
 * Returns an object in this format: { top: "20px", left: "20px" }
 */
export const getTooltipPosition = (
  mousePosition: { x: number; y: number },
  tooltipId: string,
  containerId: string
): { top: string; left: string } => {
  // default values are null so the tooltip doesn't flicker
  const returnPosition = {
    top: "",
    left: "",
  };

  const tooltipHeight = document?.getElementById(tooltipId)?.scrollHeight || 0;
  const tooltipWidth = document?.getElementById(tooltipId)?.scrollWidth || 0;
  const containerHeight =
    document?.getElementById(containerId)?.scrollHeight || 0;
  const containerWidth =
    document?.getElementById(containerId)?.scrollWidth || 0;

  // margin put between the tooltip and the mouse
  const tooltipMargin = 20;

  if (tooltipHeight && tooltipWidth) {
    // default position values are based on the mouse's xy coordinates
    returnPosition.top = `${mousePosition.y}px`;
    returnPosition.left = `${mousePosition.x}px`;

    const isOverflowingTop = mousePosition.y < tooltipHeight + tooltipMargin;
    const isOverflowingBottom =
      containerHeight < mousePosition.y + tooltipHeight;
    const isOverflowingRight = containerWidth < mousePosition.x + tooltipWidth;
    const isOverflowingLeft = mousePosition.x < tooltipWidth;
    const halfTooltipWidth = tooltipWidth / 2;
    const halfTooltipHeight = tooltipHeight / 2;

    // tooltip is overflowing the top-right of the map
    if (isOverflowingTop && isOverflowingRight) {
      returnPosition.left = `${
        mousePosition.x - halfTooltipWidth - tooltipMargin
      }px`;
      returnPosition.top = `${
        mousePosition.y + tooltipHeight + tooltipMargin
      }px`;
    }
    // tooltip is overflowing the top-left of the map
    else if (isOverflowingTop && isOverflowingLeft) {
      returnPosition.left = `${
        mousePosition.x + halfTooltipWidth + tooltipMargin
      }px`;
      returnPosition.top = `${
        mousePosition.y + tooltipHeight + tooltipMargin
      }px`;
    }
    // tooltip is overflowing the bottom-right of the map
    else if (isOverflowingBottom && isOverflowingRight) {
      returnPosition.left = `${
        mousePosition.x - halfTooltipWidth - tooltipMargin
      }px`;
      returnPosition.top = `${mousePosition.y}px`;
    }
    // tooltip is overflowing the bottom-left of the map
    else if (isOverflowingBottom && isOverflowingLeft) {
      returnPosition.left = `${
        mousePosition.x + halfTooltipWidth + tooltipMargin
      }px`;
      returnPosition.top = `${mousePosition.y}px`;
    }
    // tooltip is overflowing the top of the map only
    else if (isOverflowingTop) {
      returnPosition.top = `${
        mousePosition.y + tooltipHeight + tooltipMargin
      }px`;
    }
    // tooltip is overflowing the right side of the map only
    else if (isOverflowingRight) {
      returnPosition.left = `${
        mousePosition.x - halfTooltipWidth - tooltipMargin
      }px`;
      returnPosition.top = `${mousePosition.y + halfTooltipHeight}px`;
    }
    // tooltip is overflowing the bottom of the map only
    else if (isOverflowingBottom) {
      returnPosition.top = `${mousePosition.y}px`;
    }
    // tooltip is overflowing the left side of the map only
    else if (isOverflowingLeft) {
      returnPosition.left = `${
        mousePosition.x + halfTooltipWidth + tooltipMargin
      }px`;
      returnPosition.top = `${mousePosition.y + halfTooltipHeight}px`;
    }
  }

  return returnPosition;
};
